import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { CycleDetail, CycleReport, CycleSummary, formatDate, SIGNAL_DESCRIPTIONS, TargetType } from '../../services/learningApi';
import { credits, CycleStatusChip, HelpTip, RunLink, useApis } from './common';
import { PlanSummary } from './PlanSummary';

const TIER_NAMES: Record<number, string> = { 3: 'positive feedback', 2: 'negative / corrected', 1: 'failed / empty / repeated', 0: 'no signal' };
const TIER_HELP: Record<number, string> = {
  3: 'Someone gave this run positive feedback (thumbs up, click, purchase…). Strongest evidence of a good result.',
  2: 'Negative feedback, or the chain was re-run with adjustment notes (the notes explain what was wrong).',
  1: 'Detected automatically: the run failed, returned nothing useful, or the same input was sent again soon after.',
  0: 'Nothing was known about this run beforehand. The AI check judged it on its own.',
};
const QUALITY_COLORS: Record<string, 'success' | 'warning' | 'error'> = { good: 'success', mixed: 'warning', bad: 'error' };
const QUALITY_HELP: Record<string, string> = {
  good: 'The analyzer judged the result good. What it learned from this run counts fully.',
  mixed: 'Partly right. Learnings from this run count for less.',
  bad: 'The analyzer judged the result poor. It is used as an example of what not to do.',
};
const SIGNAL_COLUMN_HELP =
  'What was already known about the run before the AI looked at it. Runs with stronger signals are analyzed first. Hover a chip for the exact signals.';
const AI_CHECK_HELP = "The analyzer's own verdict on the run's result. It weights how much each run counts, so learning works even with no feedback.";

export const CycleReportView: React.FC<{ report: CycleReport | null | undefined }> = ({ report }) => {
  if (!report || (!report.summary && !report.what_works?.length && !report.what_fails?.length)) {
    return <Typography color="text.secondary">No report for this cycle.</Typography>;
  }
  const section = (title: string, children: React.ReactNode) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
  return (
    <Box>
      {report.summary && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.summary}</Typography>}
      {!!report.what_works?.length &&
        section(
          'What works',
          <List dense disablePadding>
            {report.what_works.map((w, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0 }}>
                <ListItemText primary={`✓ ${w}`} />
              </ListItem>
            ))}
          </List>,
        )}
      {!!report.what_fails?.length &&
        section(
          'What fails',
          <List dense disablePadding>
            {report.what_fails.map((f, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0 }}>
                <ListItemText primary={`✗ ${f.issue}`} secondary={f.frequency} />
              </ListItem>
            ))}
          </List>,
        )}
      {!!report.gaps?.length &&
        section(
          'Gaps',
          <List dense disablePadding>
            {report.gaps.map((g, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0 }}>
                <ListItemText primary={g.topic} secondary={g.examples?.join(', ')} />
              </ListItem>
            ))}
          </List>,
        )}
      {!!report.other?.length &&
        section(
          'Other notes',
          <List dense disablePadding>
            {report.other.map((o, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0 }}>
                <ListItemText primary={o} />
              </ListItem>
            ))}
          </List>,
        )}
    </Box>
  );
};

export const CycleLog: React.FC<{ log: CycleDetail['log']; maxHeight?: number }> = ({ log, maxHeight = 320 }) => (
  <Box component="pre" sx={{ m: 0, p: 1.5, maxHeight, overflow: 'auto', fontSize: 12, fontFamily: 'monospace', bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
    {log.length === 0 && 'Waiting to start…'}
    {log.map((l, i) => (
      <Box key={i} component="div" sx={{ color: l.level === 'warning' ? 'warning.main' : l.level === 'error' ? 'error.main' : undefined }}>
        {new Date(`${l.at}Z`).toLocaleTimeString()} {l.msg}
      </Box>
    ))}
  </Box>
);

export function cycleResultText(c: CycleSummary): string {
  const s = c.summary || {};
  const parts: string[] = [];
  if (s.analyzed !== undefined && s.analyzed !== null) parts.push(`${s.analyzed}${s.planned ? `/${s.planned}` : ''} runs`);
  if (s.knowledge) {
    const k = s.knowledge as Record<string, number>;
    if (k.entries_created) parts.push(`${k.entries_created} new learnings`);
    if (k.entries_activated) parts.push(`${k.entries_activated} went live`);
    if (k.entries_disputed) parts.push(`${k.entries_disputed} contradicted, back to waiting`);
  }
  if (s.suggestions && (s.suggestions as Record<string, number>).created) parts.push(`${(s.suggestions as Record<string, number>).created} suggestions`);
  return parts.join(' · ') || (c.error ? c.error : '—');
}

export const CycleDetailDialog: React.FC<{ cycleId: string; onClose: () => void }> = ({ cycleId, onClose }) => {
  const apis = useApis();
  const [cycle, setCycle] = useState<CycleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    apis.learning
      .cycle(cycleId, pageSize, page * pageSize)
      .then(setCycle)
      .catch((e) => setError(e.message));
  }, [apis, cycleId, page]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <span>Analysis cycle</span>
          {cycle && <CycleStatusChip status={cycle.status} />}
          {cycle && (
            <Typography variant="body2" color="text.secondary">
              {formatDate(cycle.started_at || cycle.created_at)} · {cycle.trigger}
              {cycle.created_by ? ` by ${cycle.created_by}` : ''}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 400 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!cycle && !error && <CircularProgress size={24} />}
        {cycle && (
          <>
            {cycle.error && (
              <Alert severity={cycle.status === 'failed' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {cycle.error}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Runs analyzed
                </Typography>
                <Typography variant="h6">
                  {cycle.summary.analyzed ?? 0} / {cycle.summary.planned ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Cost
                </Typography>
                <Typography variant="h6">{credits(cycle.credits_used)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  cap {credits(cycle.credit_cap)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  AI quality check
                  <HelpTip title={AI_CHECK_HELP} />
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {Object.entries(cycle.summary.quality || {}).map(([q, n]) => (
                    <Tooltip key={q} title={QUALITY_HELP[q] || ''}>
                      <Chip size="small" label={`${q}: ${n}`} color={QUALITY_COLORS[q]} variant="outlined" />
                    </Tooltip>
                  ))}
                  {!Object.keys(cycle.summary.quality || {}).length && <Typography variant="body2">—</Typography>}
                </Stack>
              </Grid>
            </Grid>
            {cycle.summary.stopped_early && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Stopped early: {cycle.summary.stopped_early} What was learned up to that point was kept.
              </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Report" />
              <Tab label={`Runs analyzed (${cycle.scanned.total})`} />
              <Tab label="Plan" />
              <Tab label="Log" />
            </Tabs>

            {tab === 0 && <CycleReportView report={cycle.report} />}
            {tab === 1 && (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Run</TableCell>
                        <TableCell>When</TableCell>
                        <TableCell>
                          Signal
                          <HelpTip title={SIGNAL_COLUMN_HELP} />
                        </TableCell>
                        <TableCell>
                          AI check
                          <HelpTip title={AI_CHECK_HELP} />
                        </TableCell>
                        <TableCell>Why</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cycle.scanned.items.map((it) => (
                        <TableRow key={it.source_id}>
                          <TableCell>
                            <RunLink type={it.source_type} id={it.source_id} available={it.history_available} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(it.source_created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="body2">{TIER_HELP[it.signal_tier] || ''}</Typography>
                                  {it.signals.map((s, i) => (
                                    <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                      • {SIGNAL_DESCRIPTIONS[s.provider]?.label || s.provider}: {s.label}
                                      {s.note ? ` — ${s.note}` : ''}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                            >
                              <Chip size="small" variant="outlined" label={TIER_NAMES[it.signal_tier] || it.signal_tier} />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {it.ai_quality ? (
                              <Tooltip title={QUALITY_HELP[it.ai_quality] || ''}>
                                <Chip size="small" label={it.ai_quality} color={QUALITY_COLORS[it.ai_quality]} />
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420 }}>
                            <Typography variant="body2">{it.ai_reason || ''}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {cycle.scanned.total > pageSize && (
                  <TablePagination
                    component="div"
                    count={cycle.scanned.total}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[pageSize]}
                    onPageChange={(_, p) => setPage(p)}
                  />
                )}
              </>
            )}
            {tab === 2 && (cycle.plan ? <PlanSummary plan={cycle.plan} /> : <Typography color="text.secondary">No plan recorded.</Typography>)}
            {tab === 3 && <CycleLog log={cycle.log} maxHeight={500} />}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const HistoryTab: React.FC<{ targetType: TargetType; targetId: string; hasConfig: boolean; refreshKey: number }> = ({
  targetType,
  targetId,
  hasConfig,
  refreshKey,
}) => {
  const apis = useApis();
  const [cycles, setCycles] = useState<CycleSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const pageSize = 25;

  const load = useCallback(async () => {
    if (!hasConfig) return;
    setLoading(true);
    try {
      const res = await apis.learning.cycles(targetType, targetId, pageSize, page * pageSize);
      setCycles(res.cycles);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apis, targetType, targetId, page, hasConfig]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!hasConfig) return <Alert severity="info">No analysis has run yet.</Alert>;

  return (
    <Paper variant="outlined">
      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Result</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {cycles.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No cycles yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {cycles.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(c.id)}>
                <TableCell>{formatDate(c.started_at || c.created_at)}</TableCell>
                <TableCell>
                  {c.trigger}
                  {c.created_by ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {c.created_by}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <CycleStatusChip status={c.status} />
                </TableCell>
                <TableCell sx={{ maxWidth: 420 }}>
                  <Typography variant="body2" noWrap title={cycleResultText(c)}>
                    {cycleResultText(c)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{credits(c.credits_used)}</TableCell>
                <TableCell align="right">
                  <Button size="small">Details</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {total > pageSize && (
        <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[pageSize]} onPageChange={(_, p) => setPage(p)} />
      )}
      {openId && <CycleDetailDialog cycleId={openId} onClose={() => setOpenId(null)} />}
    </Paper>
  );
};
